/**
 * WizardStepRail — Tier 0 module-local primitive per ADR
 * `frontend-0004` (`docs/adr/frontend-0004-ui-kit-doctrine.md`). Lives
 * in this module's `_components/` folder because Tier 3 promotion to
 * `@sparta/ui-kit` requires a real cross-slice import in code, which
 * does not exist on this branch. Stays at Tier 0 until that reactive
 * trigger fires.
 *
 * Visual: a horizontal `<ol>` of numbered step indicators. Each step
 * renders a circle (number for upcoming steps, brand fill for the
 * active step, success fill + checkmark for completed steps) followed
 * by an "Step N" eyebrow + the step label. Reads as a familiar
 * stepper / breadcrumb hybrid.
 *
 * Behavior:
 *  - Pure presentational; the parent passes `steps` (label list) and
 *    `current` (0-based active index).
 *  - `current` clamps at the rendered indices: steps before `current`
 *    show the completed style; the step at `current` shows the active
 *    style; steps after `current` show the upcoming style.
 *  - `aria-current="step"` marks the active step's `<li>` for screen
 *    readers; the rail is otherwise non-interactive (no click-to-jump
 *    yet — first slice). Future iteration may add an `onJump` callback
 *    for completed steps.
 */
import * as React from 'react';
import { Icon } from '@sparta/ui-kit';

export interface WizardStepRailProps {
  /** Step labels in order. Length determines the rail's segment count. */
  readonly steps: ReadonlyArray<string>;
  /** Zero-based index of the active step. */
  readonly current: number;
  /** Accessible label for the rail (e.g. "Invite teammate steps"). */
  readonly 'aria-label': string;
}

export function WizardStepRail({
  steps,
  current,
  'aria-label': ariaLabel,
}: WizardStepRailProps): React.ReactElement {
  return (
    <ol
      aria-label={ariaLabel}
      className="flex flex-wrap gap-2 list-none p-0 m-0"
    >
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li
            key={s}
            aria-current={active ? 'step' : undefined}
            className="flex items-center gap-2 min-w-36 flex-1"
          >
            <span
              aria-hidden="true"
              className={[
                'inline-flex items-center justify-center rounded-full text-xs font-bold shrink-0 w-7 h-7',
                done
                  ? 'bg-success-500 text-white'
                  : active
                    ? 'bg-brand-500 text-white'
                    : 'bg-[var(--bg-sunken)] text-[var(--fg-tertiary)]',
              ].join(' ')}
            >
              {done ? (
                <Icon name="check" size={14} strokeWidth={3} />
              ) : (
                i + 1
              )}
            </span>
            <span className="min-w-0">
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--fg-tertiary)]">
                Step {i + 1}
              </span>
              <span
                className={[
                  'block truncate text-sm whitespace-nowrap',
                  active
                    ? 'font-bold text-[var(--fg-primary)]'
                    : 'font-medium text-[var(--fg-secondary)]',
                ].join(' ')}
              >
                {s}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
